import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';

import { useTheme } from '@mui/material';

import { setPageTitle } from '../../../../redux/title/title.slice';
import { getProviderBookSummary } from '../../../../redux/aum/aum.slice';

import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

import Loader from '../../../../components/ui/loader';
import { FTable, FTableCell } from '../../../../components/ui/table';
import { Box } from '@mui/system';

function ViewProviderBook() {
  const dispatch = useDispatch();
  const theme = useTheme();

  const { id: providerId } = useParams();

  const officeId = useSelector((state) => state.auth.user.tenant_id);

  const [advisorSort, setAdvisorSort] = useState({
    field: 'name',
    direction: 'asc'
  });

  const { providerBookSummary, loadingProviderBookSummary, providerAum, bookAnalysisUI } =
    useSelector((state) => state.aum);

  const selectedProvider = useMemo(() => {
    return providerAum?.find((p) => p.id === providerId);
  }, [providerAum, providerId]);

  const title = selectedProvider?.name || 'Provider Summary';

  useEffect(() => {
    dispatch(setPageTitle(title));

    const fromDate = bookAnalysisUI?.isMonthEnd
      ? bookAnalysisUI?.selectedMonth
      : dayjs().format('YYYY-MM-DD');

    const payload = {
      officeId,
      serviceProviderId: providerId,
      fromDate
    };

    dispatch(getProviderBookSummary(payload));
  }, [dispatch, officeId, providerId, bookAnalysisUI, title]);

  const handleSort = (setter, current, field) => {
    setter({
      field,
      direction: current.field === field && current.direction === 'asc' ? 'desc' : 'asc'
    });
  };

  if (loadingProviderBookSummary) {
    return <Loader loading={true} message="Loading Provider Book..." />;
  }

  const sortedAdvisors = [...(providerBookSummary || [])].sort((a, b) => {
    const { field, direction } = advisorSort;

    let result = 0;

    if (field === 'name') {
      result = (a.name || '').localeCompare(b.name || '');
    }

    if (field === 'sumSys') {
      result = (a.sumSys || 0) - (b.sumSys || 0);
    }

    return direction === 'asc' ? result : -result;
  });

  const SortIcon = ({ active, direction }) => {
    if (!active) {
      return <ArrowUpwardIcon fontSize="small" sx={{ opacity: 0.4 }} />;
    }

    return direction === 'asc' ? (
      <ArrowUpwardIcon fontSize="small" />
    ) : (
      <ArrowDownwardIcon fontSize="small" />
    );
  };

  const columns = [
    {
      label: (
        <Box
          display="flex"
          alignItems="center"
          sx={{ cursor: 'pointer' }}
          onClick={() => handleSort(setAdvisorSort, advisorSort, 'name')}>
          Advisor
          <SortIcon active={advisorSort.field === 'name'} direction={advisorSort.direction} />
        </Box>
      )
    },
    { label: 'Code' },
    {
      label: (
        <Box
          display="flex"
          alignItems="center"
          sx={{ cursor: 'pointer' }}
          onClick={() => handleSort(setAdvisorSort, advisorSort, 'sumSys')}>
          Amount
          <SortIcon active={advisorSort.field === 'sumSys'} direction={advisorSort.direction} />
        </Box>
      )
    },
    { label: 'As Of' }
  ];

  return (
    <FTable
      title={title}
      columns={columns}
      rows={sortedAdvisors || []}
      emptyMessage="No data found"
      renderRow={(row) => (
        <>
          <FTableCell>{row.name || '—'}</FTableCell>

          <FTableCell>{row.code || '—'}</FTableCell>

          <FTableCell
            sx={{
              color: theme.palette.accent.main,
              fontWeight: 600
            }}>
            R {row.sumSys?.toLocaleString() ?? '—'}
          </FTableCell>

          <FTableCell>{row.asof ? dayjs(row.asof).format('YYYY-MM-DD') : '—'}</FTableCell>
        </>
      )}
    />
  );
}

export default ViewProviderBook;
